// LoadingBoxes.jsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes, darken } from '@mui/material/styles';
import { isEmpty } from '../../utilities/globalServiceUtils';

/* =======================
   Helpers
   ======================= */

const clampSpeed = (speed) => {
    const s = typeof speed === 'number' && !Number.isNaN(speed) ? speed : 5;
    return Math.min(10, Math.max(1, s));
};

const calcDuration = (baseSeconds, speed) => {
    const s = clampSpeed(speed);      // 1..10
    const factor = s / 5;             // 0.2..2
    const result = baseSeconds / factor;
    return `${result}s`;
};

/* =======================
   Keyframes
   ======================= */

// Original boxes
const box1 = keyframes`
  0%, 50% {
    transform: translate(100%, 0);
  }
  100% {
    transform: translate(200%, 0);
  }
`;

const box2 = keyframes`
  0% {
    transform: translate(0, 100%);
  }
  50% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(100%, 0);
  }
`;

const box3 = keyframes`
  0%, 50% {
    transform: translate(100%, 100%);
  }
  100% {
    transform: translate(0, 100%);
  }
`;

const box4 = keyframes`
  0% {
    transform: translate(200%, 0);
  }
  50% {
    transform: translate(200%, 100%);
  }
  100% {
    transform: translate(100%, 100%);
  }
`;

// Spinning cube
const cubeSpin = keyframes`
  0% {
    transform: rotateX(0deg) rotateY(0deg);
  }
  50% {
    transform: rotateX(180deg) rotateY(90deg);
  }
  100% {
    transform: rotateX(360deg) rotateY(360deg);
  }
`;

// Orbiting cubes
const orbit = keyframes`
  0% {
    transform: rotateZ(0deg) rotateX(60deg);
  }
  100% {
    transform: rotateZ(360deg) rotateX(60deg);
  }
`;

/* =======================
   Styled roots for variants
   ======================= */

// Variant: boxes (original)
const BoxesRoot = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'speed',
})(({ theme, speed }) => {
    const color = theme.palette.primary.light;
    const colorRight = darken(color, 0.15);
    const colorLeft = darken(color, 0.05);
    const shadow = '#DBE3F4';
    const duration = calcDuration(0.8, speed); // base 0.8s

    return {
        '--size': '32px',
        '--duration': duration,
        height: 'calc(var(--size) * 2)',
        width: 'calc(var(--size) * 3)',
        position: 'relative',
        transformStyle: 'preserve-3d',
        transformOrigin: '50% 50%',
        marginTop: 'calc(var(--size) * 1.5 * -1)',
        transform: 'rotateX(60deg) rotateZ(45deg) rotateY(0deg) translateZ(0px)',

        '& .box': {
            width: 'var(--size)',
            height: 'var(--size)',
            top: 0,
            left: 0,
            position: 'absolute',
            transformStyle: 'preserve-3d',

            '&:nth-of-type(1)': {
                transform: 'translate(100%, 0)',
                animation: `${box1} var(--duration) linear infinite`,
            },
            '&:nth-of-type(2)': {
                transform: 'translate(0, 100%)',
                animation: `${box2} var(--duration) linear infinite`,
            },
            '&:nth-of-type(3)': {
                transform: 'translate(100%, 100%)',
                animation: `${box3} var(--duration) linear infinite`,
            },
            '&:nth-of-type(4)': {
                transform: 'translate(200%, 0)',
                animation: `${box4} var(--duration) linear infinite`,
            },

            '& > div': {
                '--background': color,
                '--top': 'auto',
                '--right': 'auto',
                '--bottom': 'auto',
                '--left': 'auto',
                '--translateZ': 'calc(var(--size) / 2)',
                '--rotateY': '0deg',
                '--rotateX': '0deg',

                position: 'absolute',
                width: '100%',
                height: '100%',
                background: 'var(--background)',
                top: 'var(--top)',
                right: 'var(--right)',
                bottom: 'var(--bottom)',
                left: 'var(--left)',
                transform:
                    'rotateY(var(--rotateY)) rotateX(var(--rotateX)) translateZ(var(--translateZ))',

                '&:nth-of-type(1)': {
                    '--top': '0',
                    '--left': '0',
                },
                '&:nth-of-type(2)': {
                    '--background': colorRight,
                    '--right': '0',
                    '--rotateY': '90deg',
                },
                '&:nth-of-type(3)': {
                    '--background': colorLeft,
                    '--rotateX': '-90deg',
                },
                '&:nth-of-type(4)': {
                    '--background': shadow,
                    '--top': '0',
                    '--left': '0',
                    '--translateZ': 'calc(var(--size) * 3 * -1)',
                },
            },
        },
    };
});

// Variant: single spinning cube
const CubeSpinnerRoot = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'speed',
})(({ theme, speed }) => {
    const color = theme.palette.primary.light;
    const colorRight = darken(color, 0.2);
    const colorLeft = darken(color, 0.1);
    const duration = calcDuration(1.2, speed); // base 1.2s

    return {
        '--size': '48px',
        width: '80px',
        height: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        perspective: '600px',

        '& .cube': {
            position: 'relative',
            width: 'var(--size)',
            height: 'var(--size)',
            transformStyle: 'preserve-3d',
            animation: `${cubeSpin} ${duration} linear infinite`,
        },

        '& .face': {
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: color,
            opacity: 0.95,
            boxShadow: `0 6px 16px rgba(0,0,0,0.15)`,
        },

        '& .face.front': {
            transform: 'translateZ(calc(var(--size) / 2))',
        },
        '& .face.back': {
            transform: 'rotateY(180deg) translateZ(calc(var(--size) / 2))',
            background: colorRight,
        },
        '& .face.right': {
            transform: 'rotateY(90deg) translateZ(calc(var(--size) / 2))',
            background: colorRight,
        },
        '& .face.left': {
            transform: 'rotateY(-90deg) translateZ(calc(var(--size) / 2))',
            background: colorLeft,
        },
        '& .face.top': {
            transform: 'rotateX(90deg) translateZ(calc(var(--size) / 2))',
            background: colorLeft,
        },
        '& .face.bottom': {
            transform: 'rotateX(-90deg) translateZ(calc(var(--size) / 2))',
            background: darken(color, 0.25),
        },
    };
});

// Variant: orbiting cubes
const OrbitCubesRoot = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'speed',
})(({ theme, speed }) => {
    const color = theme.palette.primary.light;
    const shadow = 'rgba(0,0,0,0.12)';
    const durationOuter = calcDuration(1.3, speed); // base 1.3s
    const durationInner = calcDuration(1.0, speed); // base 1.0s

    return {
        '--size': '14px',
        width: '90px',
        height: '90px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        perspective: '600px',

        '& .orbit': {
            position: 'absolute',
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            transformStyle: 'preserve-3d',
            animation: `${orbit} ${durationOuter} linear infinite`,
        },

        '& .orbit:nth-of-type(2)': {
            width: '50px',
            height: '50px',
            animationDuration: durationInner, // faster inner orbit
            opacity: 0.9,
        },

        // FIX: scope cubes per orbit, not globally
        '& .orbit .cube': {
            position: 'absolute',
            width: 'var(--size)',
            height: 'var(--size)',
            background: color,
            borderRadius: 4,
            boxShadow: `0 4px 10px ${shadow}`,
            transformStyle: 'preserve-3d',
        },

        '& .orbit .cube:nth-of-type(1)': {
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
        },
        '& .orbit .cube:nth-of-type(2)': {
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
        },
        '& .orbit .cube:nth-of-type(3)': {
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
        },
        '& .orbit .cube:nth-of-type(4)': {
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
        },
    };
});

// Wrapper to center loaders
const Wrapper = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.background.paper,
    fontFamily: theme.typography.fontFamily,
}));

/* =======================
   Loader selector
   ======================= */

function LoaderGraphic({ variant, speed }) {
    switch (variant) {
        case 'cube-spinner':
            return (
                <CubeSpinnerRoot speed={speed}>
                    <Box className="cube">
                        <Box className="face front" />
                        <Box className="face back" />
                        <Box className="face right" />
                        <Box className="face left" />
                        <Box className="face top" />
                        <Box className="face bottom" />
                    </Box>
                </CubeSpinnerRoot>
            );

        case 'orbit-cubes':
            return (
                <OrbitCubesRoot speed={speed}>
                    <Box className="orbit">
                        <Box className="cube" />
                        <Box className="cube" />
                        <Box className="cube" />
                        <Box className="cube" />
                    </Box>
                    <Box className="orbit">
                        <Box className="cube" />
                        <Box className="cube" />
                        <Box className="cube" />
                        <Box className="cube" />
                    </Box>
                </OrbitCubesRoot>
            );

        case 'boxes':
        default:
            return (
                <BoxesRoot speed={speed}>
                    <Box className="box">
                        <Box component="div" />
                        <Box component="div" />
                        <Box component="div" />
                        <Box component="div" />
                    </Box>
                    <Box className="box">
                        <Box component="div" />
                        <Box component="div" />
                        <Box component="div" />
                        <Box component="div" />
                    </Box>
                    <Box className="box">
                        <Box component="div" />
                        <Box component="div" />
                        <Box component="div" />
                        <Box component="div" />
                    </Box>
                    <Box className="box">
                        <Box component="div" />
                        <Box component="div" />
                        <Box component="div" />
                        <Box component="div" />
                    </Box>
                </BoxesRoot>
            );
    }
}

/* =======================
   Main component
   ======================= */

function LoadingBoxes({
    text = 'Loading 3D Model...',
    subText = '',
    percentage,
    variant = 'boxes', // 'boxes' | 'cube-spinner' | 'orbit-cubes'
    speed = 5,          // 1 (slow) ... 10 (fast)
}) {
    const showPercentage =
        typeof percentage === 'number' && !Number.isNaN(percentage);

    return (
        <Wrapper>
            <Box
                sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <LoaderGraphic variant={variant} speed={speed} />

                <Typography
                    variant="body1"
                    sx={{
                        mt: 4,
                        fontWeight: 'bold',
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {text}
                    {showPercentage ? ` ${Math.round(percentage)}%` : ''}
                </Typography>
                {!isEmpty(subText) && (<>
                    <Typography
                        variant="body2"
                        color='text.secondary'
                        sx={{
                            mt: 4,
                            fontWeight: 'bold',
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        {subText}
                    </Typography>
                </>)}
            </Box>
        </Wrapper>
    );
}

export default LoadingBoxes;
